import React, { useState, useRef } from 'react';
import Papa from 'papaparse';
import { collection, writeBatch, doc } from 'firebase/firestore';
import { db } from '../../config/firebase-config';

export default function BulkSongUpload({ user, onUploadComplete, existingSongs = [] }) {
  const [isOpen, setIsOpen] = useState(false);
  const [, setCsvFile] = useState(null);
  const [parsedData, setParsedData] = useState([]);
  const [validationErrors, setValidationErrors] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({ current: 0, total: 0 });
  const [uploadResults, setUploadResults] = useState(null);
  const fileInputRef = useRef(null);

  // Generate sample CSV for download
  const generateSampleCSV = () => {
    const sampleData = [
      ['song', 'artist', 'price'],
      ['Wonderwall', 'Oasis', '25'],
      ['Hotel California', 'Eagles', '30'],
      ['Bohemian Rhapsody', 'Queen', '35'],
      ['Sweet Child O Mine', 'Guns N Roses', '25']
    ];
    
    const csvContent = sampleData.map(row => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'sample_songs.csv';
    link.click();
    URL.revokeObjectURL(url);
  };

  // Handle file selection
  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file && file.type === 'text/csv') {
      setCsvFile(file);
      parseCSV(file);
    } else {
      alert('Please select a valid CSV file');
    }
  };

  // Parse CSV file
  const parseCSV = (file) => {
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        validateData(results.data);
      },
      error: (error) => {
        console.error('Error parsing CSV:', error);
        alert('Error parsing CSV file. Please check the format.');
      }
    });
  };

  // Validate parsed data
  const validateData = (data) => {
    const errors = [];
    const validRows = [];
    
    // Check for required headers
    if (data.length > 0) {
      const headers = Object.keys(data[0]).map(h => h.toLowerCase().trim());
      const requiredHeaders = ['song', 'artist', 'price'];
      const missingHeaders = requiredHeaders.filter(h => !headers.includes(h));
      
      if (missingHeaders.length > 0) {
        errors.push(`Missing required headers: ${missingHeaders.join(', ')}`);
        setValidationErrors(errors);
        setParsedData([]);
        return;
      }
    }

    // Validate each row
    data.forEach((row, index) => {
      const rowErrors = [];
      const cleanRow = {};
      
      // Get values with case-insensitive header matching
      const songName = Object.entries(row).find(([key]) => key.toLowerCase().trim() === 'song')?.[1]?.trim();
      const artistName = Object.entries(row).find(([key]) => key.toLowerCase().trim() === 'artist')?.[1]?.trim();
      const priceStr = Object.entries(row).find(([key]) => key.toLowerCase().trim() === 'price')?.[1]?.trim();
      
      // Validate song name
      if (!songName) {
        rowErrors.push('Missing song name');
      } else {
        cleanRow.name = songName;
      }
      
      // Validate artist name
      if (!artistName) {
        rowErrors.push('Missing artist name');
      } else {
        cleanRow.artist = artistName;
      }
      
      // Validate price
      const price = parseFloat(priceStr);
      if (isNaN(price) || price < 0) {
        rowErrors.push('Invalid price (must be a non-negative number)');
      } else {
        cleanRow.price = price;
      }
      
      // Check for duplicates within CSV
      const isDuplicateInCSV = validRows.some(
        vr => vr.name === cleanRow.name && vr.artist === cleanRow.artist
      );
      
      if (isDuplicateInCSV) {
        rowErrors.push('Duplicate song in CSV');
      }
      
      // Check for duplicates in existing songs
      const isDuplicateExisting = existingSongs.some(
        song => song.name === cleanRow.name && song.artist === cleanRow.artist
      );
      
      if (isDuplicateExisting) {
        rowErrors.push('Song already exists in your library');
      }
      
      if (rowErrors.length > 0) {
        errors.push({
          row: index + 2, // +2 because index is 0-based and we have headers
          errors: rowErrors,
          data: { song: songName, artist: artistName, price: priceStr }
        });
      } else {
        validRows.push(cleanRow);
      }
    });
    
    setValidationErrors(errors);
    setParsedData(validRows);
  };

  // Upload songs to Firestore
  const handleUpload = async () => {
    if (parsedData.length === 0) {
      alert('No valid songs to upload');
      return;
    }

    setIsUploading(true);
    setUploadProgress({ current: 0, total: parsedData.length });
    
    const results = {
      successful: 0,
      failed: 0,
      errors: []
    };

    try {
      // Process in batches of 500 (Firestore limit)
      const batchSize = 500;
      for (let i = 0; i < parsedData.length; i += batchSize) {
        const batch = writeBatch(db);
        const currentBatch = parsedData.slice(i, Math.min(i + batchSize, parsedData.length));
        
        currentBatch.forEach((song) => {
          const songRef = doc(collection(db, 'songs'));
          batch.set(songRef, {
            name: song.name,
            artist: song.artist,
            price: song.price,
            musicianId: user.uid,
            createdAt: new Date(),
            uploadedVia: 'bulk_csv'
          });
        });
        
        try {
          await batch.commit();
          results.successful += currentBatch.length;
          setUploadProgress({ 
            current: Math.min(i + batchSize, parsedData.length), 
            total: parsedData.length 
          });
        } catch (error) {
          console.error('Batch upload error:', error);
          results.failed += currentBatch.length;
          results.errors.push(`Failed to upload batch starting at row ${i + 1}`);
        }
      }
      
      setUploadResults(results);
      
      if (results.successful > 0) {
        onUploadComplete();
      }
      
    } catch (error) {
      console.error('Upload error:', error);
      alert('Error uploading songs. Please try again.');
    } finally {
      setIsUploading(false);
    }
  };

  // Reset form
  const resetForm = () => {
    setCsvFile(null);
    setParsedData([]);
    setValidationErrors([]);
    setUploadResults(null);
    setUploadProgress({ current: 0, total: 0 });
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Close modal
  const handleClose = () => {
    resetForm();
    setIsOpen(false);
  };

  if (!isOpen) {
    return (
      <button
        onClick={() => setIsOpen(true)}
        className="bulk-upload-csv-btn bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2"
      >
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
        </svg>
        Bulk Upload CSV
      </button>
    );
  }

  return (
    <div style={{
      position: 'fixed',
      inset: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.7)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 50,
      padding: '16px'
    }}>
      <div style={{
        backgroundColor: '#111827', // Very dark gray
        borderRadius: '8px',
        maxWidth: '64rem',
        width: '100%',
        maxHeight: '90vh',
        overflowY: 'auto',
        border: '4px solid #7C3AED'
      }}>
        <div style={{ padding: '24px' }}>
          {/* Header */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
            <h2 style={{ fontSize: '24px', fontWeight: 'bold', color: 'white', margin: 0 }}>Bulk Song Upload</h2>
            <button
              onClick={handleClose}
              style={{ color: '#9CA3AF', background: 'none', border: 'none', cursor: 'pointer' }}
              onMouseOver={(e) => e.target.style.color = 'white'}
              onMouseOut={(e) => e.target.style.color = '#9CA3AF'}
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Upload results */}
          {uploadResults && (
            <div className={`mb-6 p-4 rounded-lg border ${uploadResults.successful > 0 ? 'bg-green-900 bg-opacity-30 border-green-500' : 'bg-red-900 bg-opacity-30 border-red-500'}`}>
              <h3 className="font-semibold mb-2 text-white">Upload Complete!</h3>
              <p className="text-green-300">✅ Successfully uploaded: {uploadResults.successful} songs</p>
              {uploadResults.failed > 0 && (
                <p className="text-red-300">❌ Failed: {uploadResults.failed} songs</p>
              )}
              {uploadResults.errors.length > 0 && (
                <div className="mt-2">
                  <p className="font-semibold text-red-300">Errors:</p>
                  <ul className="list-disc list-inside">
                    {uploadResults.errors.map((error, idx) => (
                      <li key={idx} className="text-sm text-red-400">{error}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          {/* Instructions */}
          {!uploadResults && (
            <div style={{
              marginBottom: '24px',
              padding: '16px',
              backgroundColor: '#1E3A8A',
              border: '1px solid #3B82F6',
              borderRadius: '8px'
            }}>
              <h3 style={{
                fontWeight: 'bold',
                marginBottom: '8px',
                color: '#93C5FD',
                fontSize: '16px'
              }}>Instructions:</h3>
              <ol style={{
                listStyleType: 'decimal',
                listStylePosition: 'inside',
                color: '#E5E7EB',
                fontSize: '14px',
                lineHeight: '1.5'
              }}>
                <li style={{ marginBottom: '4px' }}>Download the sample CSV template to see the correct format</li>
                <li style={{ marginBottom: '4px' }}>Fill in your songs with columns: song, artist, price</li>
                <li style={{ marginBottom: '4px' }}>Save as CSV format (not Excel)</li>
                <li style={{ marginBottom: '4px' }}>Upload your file and review the preview</li>
                <li style={{ marginBottom: '4px' }}>Click "Upload Songs" to add them to your library</li>
              </ol>
              <button
                onClick={generateSampleCSV}
                style={{
                  marginTop: '12px',
                  color: '#60A5FA',
                  textDecoration: 'underline',
                  fontSize: '14px',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer'
                }}
                onMouseOver={(e) => e.target.style.color = '#93C5FD'}
                onMouseOut={(e) => e.target.style.color = '#60A5FA'}
              >
                📥 Download Sample CSV Template
              </button>
            </div>
          )}

          {/* File upload */}
          {!uploadResults && (
            <div style={{ marginBottom: '24px' }}>
              <label style={{
                display: 'block',
                marginBottom: '8px',
                fontWeight: 'bold',
                color: 'white',
                fontSize: '16px'
              }}>Select CSV File:</label>
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv"
                onChange={handleFileSelect}
                style={{
                  display: 'block',
                  width: '100%',
                  fontSize: '14px',
                  color: '#E5E7EB',
                  backgroundColor: '#374151',
                  border: '2px solid #6B7280',
                  borderRadius: '8px',
                  padding: '8px 12px'
                }}
              />
            </div>
          )}

          {/* Upload progress */}
          {isUploading && (
            <div className="mb-6">
              <div className="flex justify-between text-sm mb-1 text-white">
                <span>Uploading songs...</span>
                <span>{uploadProgress.current} / {uploadProgress.total}</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div 
                  className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${(uploadProgress.current / uploadProgress.total) * 100}%` }}
                />
              </div>
            </div>
          )}

          {/* Validation errors */}
          {validationErrors.length > 0 && !uploadResults && (
            <div className="mb-6">
              <h3 className="font-semibold mb-2 text-red-400">Validation Errors:</h3>
              <div className="max-h-48 overflow-y-auto border border-red-500 bg-red-900 bg-opacity-20 rounded p-2">
                {typeof validationErrors[0] === 'string' ? (
                  <p className="text-red-400">{validationErrors[0]}</p>
                ) : (
                  validationErrors.map((error, idx) => (
                    <div key={idx} className="mb-2 text-sm">
                      <span className="font-semibold text-red-300">Row {error.row}:</span>
                      <ul className="list-disc list-inside ml-4">
                        {error.errors.map((e, i) => (
                          <li key={i} className="text-red-400">{e}</li>
                        ))}
                      </ul>
                      <div className="ml-4 text-gray-400">
                        Song: {error.data.song || 'N/A'}, 
                        Artist: {error.data.artist || 'N/A'}, 
                        Price: {error.data.price || 'N/A'}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}

          {/* Preview table */}
          {parsedData.length > 0 && !uploadResults && (
            <div className="mb-6">
              <h3 className="font-semibold mb-2 text-white">Preview ({parsedData.length} valid songs):</h3>
              <div className="border border-gray-600 rounded-lg overflow-hidden">
                <div className="max-h-64 overflow-y-auto">
                  <table className="w-full bg-gray-800">
                    <thead className="bg-gray-900 sticky top-0">
                      <tr>
                        <th className="text-left p-2 border-b border-gray-600 text-gray-200">Song</th>
                        <th className="text-left p-2 border-b border-gray-600 text-gray-200">Artist</th>
                        <th className="text-left p-2 border-b border-gray-600 text-gray-200">Price ($)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {parsedData.slice(0, 10).map((row, idx) => (
                        <tr key={idx} className="hover:bg-gray-600 bg-gray-800">
                          <td className="p-2 border-b border-gray-600 text-white">{row.name}</td>
                          <td className="p-2 border-b border-gray-600 text-white">{row.artist}</td>
                          <td className="p-2 border-b border-gray-600 text-white">${row.price.toFixed(2)}</td>
                        </tr>
                      ))}
                      {parsedData.length > 10 && (
                        <tr>
                          <td colSpan="3" className="p-2 text-center text-gray-400">
                            ... and {parsedData.length - 10} more songs
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* Action buttons */}
          <div className="flex justify-end gap-3">
            {uploadResults ? (
              <button
                onClick={handleClose}
                className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
              >
                Done
              </button>
            ) : (
              <>
                <button
                  onClick={handleClose}
                  className="px-6 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-500"
                >
                  Cancel
                </button>
                {parsedData.length > 0 && (
                  <button
                    onClick={handleUpload}
                    disabled={isUploading}
                    className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isUploading ? 'Uploading...' : `Upload ${parsedData.length} Songs`}
                  </button>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}